import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FileQuestion } from "lucide-react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";

interface ScorecardData {
  candidate_name: string;
  role: string;
  company: string;
  status: string;
  ready: boolean;
  html: string;
  overall: number | null;
  verdict: string;
}

const T = {
  ink: "#17131F",
  slate: "#7C7590",
  border: "#E8E4DC",
  paper: "#FBFAFE",
  white: "#FFFFFF",
  accent: "var(--accent)",
  good: "#22A06B",
};

export function RecruiterScorecard() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ScorecardData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    api
      .get<ScorecardData>(`/recruiter/scorecard/${sessionId}`)
      .then(setData)
      .catch((err: Error & { status?: number }) => {
        if (err.status === 401 || err.status === 403) {
          navigate("/auth/login", { replace: true });
        } else {
          setError(err.message ?? "Could not load scorecard.");
        }
      });
  }, [sessionId, navigate]);

  return (
    <RecruiterShell activeNav={"candidates"}>
      {/* Left-anchored, width-capped — matches every other recruiter page's
          convention (Dashboard, Candidates, Send Invite, Settings, Brand
          Setup all left-anchor; this was the one outlier centring its
          whole column). */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 16px 64px" }}>
        {error && (
          <div
            style={{
              background: T.white, borderRadius: 18, padding: "40px 32px",
              textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                width: 56, height: 56, borderRadius: "50%", background: "rgba(185,28,28,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
              }}
            >
              <FileQuestion size={26} strokeWidth={1.75} color="#B91C1C" />
            </div>
            <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: T.ink }}>
              Scorecard not available
            </p>
            <p style={{ margin: "0 0 20px", fontSize: 13.5, color: T.slate }}>{error}</p>
            <Link to="/coach/recruiter/candidates" style={{ fontSize: 13.5, fontWeight: 600, color: T.accent, textDecoration: "none" }}>
              ← Back to candidates
            </Link>
          </div>
        )}
        {!data && !error && (
          <p style={{ color: T.slate, fontSize: 14 }}>Loading…</p>
        )}
        {data && (
          <>
            <div style={{ marginBottom: 24 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.slate }}>
                Scorecard
              </p>
              <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700, color: T.ink }}>
                {data.candidate_name}
              </h1>
              {(data.role || data.company) && (
                <p style={{ margin: "4px 0 0", fontSize: 14, color: T.slate }}>
                  {[data.role, data.company].filter(Boolean).join(" · ")}
                </p>
              )}
              {data.overall != null && (
                <p style={{ margin: "12px 0 0", fontSize: 20, fontWeight: 700, color: T.good }}>
                  {data.overall}/10
                </p>
              )}
              {data.ready && (
                <a
                  href={`/coach/recruiter/scorecard/${sessionId}/export`}
                  style={{
                    display: "inline-block",
                    marginTop: 12,
                    fontSize: 13,
                    fontWeight: 600,
                    color: T.accent,
                    textDecoration: "none",
                  }}
                >
                  Download (.md) ↓
                </a>
              )}
            </div>

            {!data.ready && (
              <div
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: "32px 24px",
                  textAlign: "center",
                  color: T.slate,
                  fontSize: 14,
                }}
              >
                {data.status === "scoring" || data.status === "active"
                  ? "Generating scorecard — check back in a moment."
                  : "No scorecard available yet."}
              </div>
            )}

            {data.ready && (
              <div
                className="prose-scorecard"
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: "28px 24px",
                }}
                dangerouslySetInnerHTML={{ __html: data.html }}
              />
            )}

            {data.ready && (
              <p style={{ margin: "12px 0 0", fontSize: 13, color: T.slate }}>
                This score is AI-generated practice feedback for the candidate. It must not be used as the sole basis for a hiring decision.
              </p>
            )}

            <div style={{ marginTop: 24 }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 13,
                  color: T.accent,
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                ← Back
              </button>
            </div>
          </>
        )}
      </div>
    </RecruiterShell>
  );
}
